import React, { useState } from 'react'
import { Badge, Box, Menu, MenuItem } from '@mui/material'
import { Edit } from '@mui/icons-material'
import { StyledBadgeButton } from '../../styles/ProfileViewStyles'
import { t } from 'i18next'
import { useNotification } from '../../store/redux/hooks'
import AvatarComponent from './AvatarComponent'
import { environment } from '../../enviroments/enviroment'

type AvatarProps = {
  imageSize?: number
  iconSize?: number
  buttonSize?: number
  alt: string | undefined
  editable?: boolean
  updateAvatar?: (avatar: string) => void
  updateExtension?: (extension: string) => void
  onUpload?: (file: File) => void
  onRemove?: () => void
  extension: string | undefined
  avatar: string | undefined
  onChange: (avatar: string, extension: string) => void
}

const ProjectPicture: React.FC<AvatarProps> = ({
  imageSize = 75,
  iconSize = 18,
  buttonSize = 36,
  alt,
  editable,
  updateAvatar,
  updateExtension,
  onUpload,
  onRemove,
  avatar,
  extension,
  onChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [uploading, setUploading] = useState(false)
  const { displayNotification } = useNotification()

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const MAX_ICON_SIZE_IN_MB = (environment.maxIconSizeMb as unknown as number) || 0.01

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      const selectedImage = files[0]
      const fileSizeInMB = selectedImage.size / (1024 * 1024)

      if (fileSizeInMB > MAX_ICON_SIZE_IN_MB) {
        displayNotification({ message: t('fileSizeError', { fileSize: MAX_ICON_SIZE_IN_MB * 1024 }), type: 'error' })
        return
      }

      setUploading(true)
      const reader = new FileReader()
      reader.onload = async e => {
        try {
          const base64String = e.target?.result as string
          setAnchorEl(null)
          const imageType = selectedImage.type.split('/').pop() as string
          if (updateAvatar && updateExtension) {
            updateAvatar(base64String.split(',')[1])
            updateExtension(imageType.toUpperCase())
            onChange(base64String.split(',')[1], imageType.toUpperCase())
          }
          if (onUpload) {
            onUpload(selectedImage)
          }
          setUploading(false)
        } catch (error) {
          setUploading(false)
          displayNotification({ message: `${t('page.users.message.error')}`, type: 'error' })
        }
      }
      reader.readAsDataURL(selectedImage)
    }
  }

  const handleAvatarReset = () => {
    setAnchorEl(null)
    if (updateAvatar && updateExtension) {
      updateAvatar('')
      updateExtension('')
      onChange('', '')
    }
    if (onRemove) {
      onRemove()
    }
  }

  return (
    <Box sx={{ width: imageSize, height: imageSize }}>
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          editable &&
          !uploading && (
            <>
              <StyledBadgeButton
                sx={{ width: buttonSize, height: buttonSize }}
                onClick={handleClick}
                aria-label={t('aria.myProfile.editAvatarBadgeButton')}
              >
                <Edit sx={{ width: iconSize, color: 'text.highEmphasis' }} />
              </StyledBadgeButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem
                  autoFocus
                  component={'label'}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      document.getElementById('avatar')?.click()
                    }
                  }}
                >
                  {t('page.usersProfile.profilePicture.upload')}
                  <input type="file" id={'avatar'} style={{ display: 'none' }} onChange={handleFileChange} />
                </MenuItem>

                {avatar && (
                  <MenuItem onClick={handleAvatarReset}>{t('page.usersProfile.profilePicture.remove')}</MenuItem>
                )}
              </Menu>
            </>
          )
        }
      >
        <AvatarComponent extension={extension} avatar={avatar} size={imageSize} alt={alt} />
      </Badge>
    </Box>
  )
}

export default ProjectPicture
